import {
  COOKIE_MT_BUNDLE_ID,
  COOKIE_MT_REFERRER,
  COOKIE_OID,
  COOKIE_RSM_QUERY,
  COOKIE_TMR_LVID,
  COOKIE_YM_UID,
  getCookie,
  setCookie,
  waitForCookies,
} from '../cookies';

type Subs = [string, string, string];

type PixelLinkData = {
  src: string;
  subs: Subs;
};

const formatMtCreative = (...args: Array<string | null | undefined>) =>
  `[${args.map((x) => x || 'undefined')}]`;

const RSM_KEY = 'rsm';
const MT_LINK_ID_KEY = 'mt_link_id';
export const MT_DEEPLINK = 'mt_deeplink';
const MT_CREATIVE = 'mt_creative';

const TRACKING_LINK_BASE = 'https://trk.mail.ru/c/';
const UNLABELED_COMPANY_ID = 'nubrn2';

const getSubs = (params: URLSearchParams): Subs => {
  const subs: Subs = ['', '', ''];
  for (let i = 0; i < subs.length; i++) {
    subs[i] = params.get(`mt_sub${i + 1}`) ?? '';
  }
  return subs;
};

const generatePixelLinkForLabeledUser = (
  queryParams: URLSearchParams,
  getDeferredDeepLink: (bundleId: string) => string,
): PixelLinkData => {
  const params = new URLSearchParams(queryParams);
  const mtLinkId = params.get(MT_LINK_ID_KEY) as string;
  const mtBundleId = getCookie(COOKIE_MT_BUNDLE_ID);

  params.delete(RSM_KEY);
  params.delete(MT_LINK_ID_KEY);
  params.append('mt_no_redirect', '1');

  if (mtBundleId) {
    params.append(MT_DEEPLINK, getDeferredDeepLink(mtBundleId));
  }

  params.append(
    MT_CREATIVE,
    formatMtCreative(
      getCookie(COOKIE_YM_UID),
      getCookie(COOKIE_TMR_LVID),
      getCookie(COOKIE_OID),
    ),
  );

  return {
    src: `${TRACKING_LINK_BASE}${mtLinkId}?${decodeURIComponent(
      params.toString(),
    )}`,
    subs: getSubs(params),
  };
};

const generatePixelLinkForUnlabeledUser = (
  getDeferredDeepLink: (bundleId: string) => string,
): PixelLinkData => {
  const url = new URL(`${TRACKING_LINK_BASE}${UNLABELED_COMPANY_ID}`);
  url.searchParams.append('mt_no_redirect', '1');

  const referrer = getCookie(COOKIE_MT_REFERRER);

  if (referrer) {
    url.searchParams.append('mt_sub1', referrer.split('?')[0]);
  }

  const ymUid = getCookie(COOKIE_YM_UID);

  if (ymUid) {
    url.searchParams.append('mt_sub2', ymUid);
  }

  const lvid = getCookie(COOKIE_TMR_LVID);

  if (lvid) {
    url.searchParams.append('mt_sub3', lvid);
  }

  const mtBundleId = getCookie(COOKIE_MT_BUNDLE_ID);

  if (mtBundleId) {
    url.searchParams.append('mt_deeplink', getDeferredDeepLink(mtBundleId));
  }

  url.searchParams.append(
    MT_CREATIVE,
    formatMtCreative(ymUid, lvid, getCookie(COOKIE_OID)),
  );

  return {
    src: decodeURIComponent(url.href),
    subs: getSubs(url.searchParams),
  };
};

type PixelRequest = {
  cookieDomain: string;
  userIp: string;
  serviceVersion: string;
  appDeeplinkBaseUrl: string; // for bundle_id
  emitPixelEvents: (payload: Record<string, unknown>) => void;
};

const isExternalReferrer = (referrer: string, domain?: string) =>
  referrer && domain && !referrer.includes(domain);

const prepareRsmCookies = (cookieDomain: string) => {
  const searchParams = new URLSearchParams(document.location.search);
  const isRsm = searchParams.get(RSM_KEY) === '1';
  const isMtLinkId = searchParams.has(MT_LINK_ID_KEY);

  if (isRsm && isMtLinkId) {
    setCookie(COOKIE_RSM_QUERY, searchParams.toString(), cookieDomain);
  }

  if (isExternalReferrer(document?.referrer, cookieDomain)) {
    setCookie(
      COOKIE_MT_REFERRER,
      encodeURIComponent(document.referrer.split('?')[0]),
      cookieDomain,
    );
  }
};

export const generateMyTrackerPixel = async ({
  cookieDomain,
  userIp,
  serviceVersion,
  appDeeplinkBaseUrl,
  emitPixelEvents,
}: PixelRequest) => {
  if (typeof document === 'undefined') {
    return;
  }

  prepareRsmCookies(cookieDomain);

  await waitForCookies({names: [COOKIE_YM_UID, COOKIE_TMR_LVID, COOKIE_OID]});

  const existingPixel = document.getElementById('pixel');

  if (existingPixel) {
    return;
  }

  const pixel = document.createElement('img');
  pixel.id = 'pixel';
  pixel.width = 1;
  pixel.height = 1;
  pixel.alt = '';
  pixel.style.display = 'none';

  const rsmCookieValue = getCookie(COOKIE_RSM_QUERY);

  const rsmQueryParams = new URLSearchParams(rsmCookieValue ?? '');

  const getDeferredDeepLink = (bundleId: string) =>
    [appDeeplinkBaseUrl, 'app', bundleId].join('/');

  const mtLinkId = rsmQueryParams.has(MT_LINK_ID_KEY)
    ? (rsmQueryParams.get(MT_LINK_ID_KEY) as string)
    : UNLABELED_COMPANY_ID;

  const {src, subs} = rsmQueryParams.has(MT_LINK_ID_KEY)
    ? generatePixelLinkForLabeledUser(rsmQueryParams, getDeferredDeepLink)
    : generatePixelLinkForUnlabeledUser(getDeferredDeepLink);

  pixel.src = src;

  document.getElementsByTagName('body')[0].appendChild(pixel);

  emitPixelEvents({
    mt_link_id: mtLinkId,
    ip_address: userIp,
    ...subs.reduce(
      (acc, value, index) => {
        acc[`mt_sub${index + 1}`] = value ?? '';
        return acc;
      },
      {} as Record<string, string>,
    ),
    service_version: serviceVersion ?? '',
  });
};

export const appendParameterToMyTrackerPixel = (key: string, value: string) => {
  const existingPixel = document.getElementById('pixel') as HTMLImageElement;

  if (!existingPixel?.src) {
    return;
  }

  const url = new URL(existingPixel.src);
  url.searchParams.set(key, value);
  existingPixel.src = url.toString();
};
